import React from 'react';

import SettingRow from 'flow/components/Setting/SettingRow';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { setHandoverToDesktop } from 'flow/companion/companion-actions';
import { isCompanionCapableSurface } from 'flow/companion/companion-utils';
import { selectHandoverToDesktop } from 'flow/selectors/app-selectors';
import { getAppDownloadUrls } from 'flow/utils/app-utils';

/**
 * "Handle calls through desktop app" - the one companion setting a surface shows.
 *
 * A toggle rather than an entry in the call-method list, because it answers a different question.
 * The call methods say how this client places a call when it places one itself; this says whether
 * it should hand the call to the machine the user is sitting at instead. Making the desktop app a
 * call method would have taken the softphone away when it was chosen - and the softphone is exactly
 * what has to keep working when no desktop app is there, which is why every other call-handling
 * setting stays put beside this one.
 *
 * Shown only where handover is possible at all, so it never appears on the desktop app itself: the
 * machine taking the calls has no use for a switch about handing them over.
 */
const HandoverToDesktopSetting = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const companionCapable = useAppSelector(isCompanionCapableSurface);
    const handover = useAppSelector(selectHandoverToDesktop);

    if (!companionCapable) {
        return null;
    }

    const downloadUrl = getAppDownloadUrls()?.website;

    const information = (
        <>
            {i18next.t('settings.companion.handover.hint')}
            {downloadUrl ? (
                <>
                    {' '}
                    <a
                        className="text-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={downloadUrl}
                    >
                        {i18next.t('settings.companion.handover.download')}
                    </a>
                </>
            ) : null}
        </>
    );

    return (
        <SettingRow
            dataTestId="handover-to-desktop"
            information={information}
            cta={
                <Toggle
                    value={handover}
                    onChange={(enabled: boolean) =>
                        dispatch(setHandoverToDesktop(enabled))
                    }
                    text={i18next.t('settings.companion.handover')}
                />
            }
        >
            {i18next.t('settings.companion.handover')}
        </SettingRow>
    );
};

export default HandoverToDesktopSetting;
